import type { PseoContent } from './content';

// Copy rules: problem → agitation → solution. Stats are product facts, never invented
// performance numbers. Only claim features that exist in the dashboard.
export const industryContent: Record<string, PseoContent> = {
  'real-estate': {
    headline: 'Your Listings Get Visitors at Midnight. Give Them Someone to Talk To.',
    subheadline:
      'A 3D AI mascot greets buyers on your property site, answers listing questions out loud, qualifies intent and books viewings into your calendar, while your agents sleep.',
    features: [
      {
        title: 'Answers from your listings',
        body: 'Feed it your listing pages, brochures and FAQs. It answers floor plans, pricing and neighbourhood questions from your content, never from the open internet.',
      },
      {
        title: 'Qualifies before you call',
        body: 'When a buyer shows intent, it asks for name, email and phone, so your agents only speak to people who are ready.',
      },
      {
        title: 'Books viewings in the chat',
        body: 'Connect Calendly or Cal.com and the mascot offers real slots and confirms the viewing. No more email back-and-forth.',
      },
    ],
    painPoints: [
      {
        problem: 'Buyers browse in the evening, when nobody answers',
        solution: 'Your mascot is on duty 24/7 and turns late-night browsing into booked viewings.',
      },
      {
        problem: 'Agents lose hours on leads that were never serious',
        solution: 'It qualifies budget, timing and intent first, and your team gets the conversation summary.',
      },
      {
        problem: 'Listing pages are silent and every agency looks the same',
        solution: 'A talking 3D host makes your site the one buyers remember.',
      },
    ],
    stats: [
      { value: '24/7', label: 'Lead capture' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
      { value: '0', label: 'Lines of code to install' },
    ],
    faqs: [
      {
        question: 'How does an AI mascot help real estate agents get more leads?',
        answer: 'It sits on your property site and talks to every visitor, answering listing questions, detecting buying intent and capturing contact details. Leads come in pre-qualified, even outside office hours, before an agent steps in.',
      },
      {
        question: 'Can it be trained on my own listings?',
        answer: 'Yes. Add your listing URLs, brochures and FAQs to the knowledge base. It answers only from that content, and it flags questions it could not answer so you can fill the gap.',
      },
      {
        question: 'How fast can I put it on my agency website?',
        answer: 'Usually in minutes. You design the mascot, add your content and paste one snippet, or use the WordPress plugin. No developer needed.',
      },
    ],
    ctaHeadline: 'Turn late-night browsers into booked viewings',
    ctaBody: 'Create your real estate mascot free. Live on your site in minutes.',
  },

  healthcare: {
    headline: 'Patients Have Questions Before They Call. Answer Them on Your Site.',
    subheadline:
      'A friendly 3D AI mascot answers practical questions about opening hours, services, insurance and preparation, books appointments and hands off to your staff when it matters.',
    features: [
      {
        title: 'Practical answers, your content only',
        body: 'It answers from the pages and documents you approve, like services, hours, preparation instructions and accepted insurance. You set its scope, and clinical questions go to your staff.',
      },
      {
        title: 'Appointment booking',
        body: 'Connect Calendly or Cal.com so patients can book a slot in the conversation instead of waiting on the phone line.',
      },
      {
        title: 'Escalation to your team',
        body: 'When a question needs a human, it opens a ticket in your helpdesk with the full conversation, so nobody has to repeat themselves.',
      },
    ],
    painPoints: [
      {
        problem: 'The phone rings all day with the same questions',
        solution: 'The mascot handles the repetitive practical questions online, so your front desk can focus on the patients in the room.',
      },
      {
        problem: 'Patients give up when they cannot reach anyone',
        solution: 'It is available every hour of the day and points them to the right next step.',
      },
      {
        problem: 'Clinical questions need a professional, not a bot',
        solution: 'You set the scope. Anything clinical is routed to your staff, not answered by AI.',
      },
    ],
    stats: [
      { value: '24/7', label: 'Patient questions answered' },
      { value: '7', label: 'Helpdesks for escalation' },
      { value: 'Scoped', label: 'To your approved content' },
    ],
    faqs: [
      {
        question: 'Can an AI mascot give medical advice?',
        answer: 'No, and it should not. You define its scope in the knowledge base, which covers practical information about your practice. Clinical or sensitive questions are escalated to your staff.',
      },
      {
        question: 'Can patients book appointments through it?',
        answer: 'Yes. Connect Calendly or Cal.com and the mascot offers available slots and confirms the booking during the conversation.',
      },
      {
        question: 'How is patient data handled?',
        answer: 'Data is encrypted in transit and at rest, and conversations are never used to train foundation models. Check your own regulatory obligations (such as HIPAA or GDPR) before collecting health information through any chat tool.',
      },
    ],
    ctaHeadline: 'Give patients answers before they pick up the phone',
    ctaBody: 'Create your practice mascot free. No coding, no long integration.',
  },

  education: {
    headline: "Students Don't Wait for Office Hours. Your Mascot Doesn't Make Them.",
    subheadline:
      'A 3D AI mascot trained on your course materials answers student questions out loud, explains concepts and points learners to the right resource. Day and night.',
    features: [
      {
        title: 'Trained on your curriculum',
        body: 'Add syllabi, course pages and FAQs. It answers from your materials, so it stays aligned with what you actually teach.',
      },
      {
        title: 'A character students remember',
        body: 'A talking mascot with a voice and a personality gets noticed in a way a text box never does.',
      },
      {
        title: 'Knows what it does not know',
        body: 'The dashboard lists the questions it could not answer and the places your materials contradict each other.',
      },
    ],
    painPoints: [
      {
        problem: 'Students get stuck at 11pm and give up',
        solution: 'The mascot answers from your course content at any hour, with endless patience.',
      },
      {
        problem: 'Instructors answer the same questions every week',
        solution: 'Deadlines, logistics and recurring questions get handled, so teachers can spend their time teaching.',
      },
      {
        problem: 'Online courses feel lonely',
        solution: 'A friendly voice on the page makes self-paced learning feel guided.',
      },
    ],
    stats: [
      { value: '24/7', label: 'Student support' },
      { value: '5+', label: 'Knowledge sources: files, web, Q&A, Notion…' },
      { value: '1', label: 'Snippet for any LMS page' },
    ],
    faqs: [
      {
        question: 'Can the mascot be trained on my specific curriculum?',
        answer: 'Yes. Upload course materials, add your course pages or connect Notion. It answers from that content, not from generic internet knowledge.',
      },
      {
        question: 'Does it work on my LMS or course platform?',
        answer: 'If you can add a script or embed to a page, yes. It is one snippet, and there are guides for WordPress, Webflow, Framer, Wix and Squarespace.',
      },
      {
        question: 'How do I know if it answered something wrong?',
        answer: 'Every conversation is logged in Activity, visitors can rate answers, and the Suggestions tab flags unanswered questions and contradictions in your content.',
      },
    ],
    ctaHeadline: 'Give every learner a guide that never logs off',
    ctaBody: 'Create your education mascot free. Trained on your content in minutes.',
  },

  ecommerce: {
    headline: 'Your Store Has Visitors. Give Them a Shop Assistant.',
    subheadline:
      'A 3D AI mascot greets shoppers, answers sizing, shipping and return questions out loud, browses your Shopify catalog and tracks orders. It is the in-store experience, online.',
    features: [
      {
        title: 'Knows your catalog',
        body: 'With the Shopify integration it browses products, checks cart status and tracks orders in real time.',
      },
      {
        title: 'Removes the last doubt',
        body: 'Sizing, delivery times, returns. It answers the question that stops the purchase, at the moment it is asked.',
      },
      {
        title: 'Handles "where is my order?"',
        body: 'Order tracking questions get answered instantly instead of piling up in your inbox.',
      },
    ],
    painPoints: [
      {
        problem: 'Shoppers leave with one unanswered question',
        solution: 'The mascot answers it out loud, right on the product page.',
      },
      {
        problem: 'A grey chat bubble looks like every other store',
        solution: 'A branded 3D character makes your shop feel staffed and gives it a personality.',
      },
      {
        problem: 'Support explodes during sales and launches',
        solution: 'Repetitive order and policy questions get absorbed without extra hires.',
      },
    ],
    stats: [
      { value: '24/7', label: 'Shop assistant' },
      { value: '3', label: 'Shopify actions: products, orders, cart' },
      { value: '5 min', label: 'To install on your theme' },
    ],
    faqs: [
      {
        question: 'Does Minimoes work with Shopify?',
        answer: 'Yes. Paste the snippet into your theme.liquid (it works with all themes, including Dawn) and turn on the Shopify action so the mascot can browse products, track orders and check cart status.',
      },
      {
        question: 'What about WooCommerce?',
        answer: 'Install the official WordPress plugin. The mascot runs on any WooCommerce store and answers from your product pages and policies.',
      },
      {
        question: 'Can it recommend products?',
        answer: 'Yes. It asks what the shopper needs and suggests products from your catalog and content, like a knowledgeable sales associate.',
      },
    ],
    ctaHeadline: 'Put a shop assistant on every product page',
    ctaBody: 'Create your store mascot free. Install it on your theme in minutes.',
  },

  finance: {
    headline: 'Explain Your Products 24/7, Without Going Off-Script.',
    subheadline:
      'A 3D AI mascot answers questions about your accounts, fees and application steps, using only the content you approved. Anything sensitive goes straight to a human.',
    features: [
      {
        title: 'Approved content only',
        body: 'It answers from the documents and pages you upload. If the answer is not in there, it says so and escalates.',
      },
      {
        title: 'Guides applications',
        body: 'It walks customers through the steps in plain language, so fewer of them drop out halfway.',
      },
      {
        title: 'Human handoff with context',
        body: 'It opens a ticket in Salesforce, Zendesk, HubSpot or your helpdesk, with the full transcript attached.',
      },
    ],
    painPoints: [
      {
        problem: 'Customers abandon applications they do not understand',
        solution: 'Plain-language answers at every step keep them moving.',
      },
      {
        problem: 'Your contact centre repeats the same explanations all day',
        solution: 'The mascot handles the recurring questions and routes the rest.',
      },
      {
        problem: 'You cannot afford an assistant that improvises',
        solution: 'Every answer comes from your source material, and every conversation is logged.',
      },
    ],
    stats: [
      { value: 'Scoped', label: 'To your approved content' },
      { value: '7', label: 'Helpdesks for escalation' },
      { value: 'Full', label: 'Conversation logs' },
    ],
    faqs: [
      {
        question: 'How do I keep the mascot within compliance boundaries?',
        answer: 'It answers only from the content you provide. Keep that content approved and up to date, review conversations in Activity, and route anything outside its scope to your advisors.',
      },
      {
        question: 'Can it guide customers through an application?',
        answer: 'Yes. It explains each step and the documents needed, and links to your application flow with a custom button.',
      },
      {
        question: 'What happens with an out-of-scope question?',
        answer: 'It escalates to a human, either as a ticket in your helpdesk or as a live Salesforce chat handoff, with the conversation attached.',
      },
    ],
    ctaHeadline: 'Answer every customer, stay on script',
    ctaBody: 'Create your mascot free. You decide exactly what it knows.',
  },

  hospitality: {
    headline: 'A Concierge That Never Clocks Out.',
    subheadline:
      'A 3D AI mascot welcomes guests on your site, answers check-in, parking and amenity questions out loud, suggests your restaurant and spa, and books when they are ready.',
    features: [
      { title: 'Pre-arrival answers', body: 'Check-in times, parking, pets, breakfast. It answers from your own pages before guests even think of calling.' },
      { title: 'Upsell at the right moment', body: 'It suggests upgrades, dining and spa from your content, with a button to book.' },
      { title: 'A character guests remember', body: 'Give your hotel a host with its own look and voice that fits your brand.' },
    ],
    painPoints: [
      { problem: 'Reception answers the same questions all day', solution: 'The mascot takes the FAQ load so your team can focus on guests in the lobby.' },
      { problem: 'Add-ons go unsold because nobody mentions them', solution: 'Your mascot brings them up naturally in the conversation.' },
      { problem: 'Hotel websites all look alike', solution: 'A talking concierge makes yours the one guests remember.' },
    ],
    stats: [
      { value: '24/7', label: 'Concierge on your site' },
      { value: '9', label: 'Multilingual voices' },
      { value: '5 min', label: 'To go live' },
    ],
    faqs: [
      { question: 'How does an AI mascot improve the guest experience?', answer: 'Guests get instant answers about their stay at any hour, from your own information, with a friendly host instead of a contact form.' },
      { question: 'Can it promote dining, spa or room upgrades?', answer: 'Yes. Add your services to the knowledge base and use custom buttons to link to booking pages when a guest is interested.' },
      { question: 'Does it speak other languages?', answer: 'The voices are multilingual, so international guests can be greeted and answered in their language.' },
    ],
    ctaHeadline: 'Give every guest a concierge',
    ctaBody: 'Create your hotel mascot free. No IT team required.',
  },

  insurance: {
    headline: 'Policies Are Confusing. Your Mascot Makes Them Clear.',
    subheadline:
      'A 3D AI mascot explains coverage, exclusions and claim steps in plain language, from your own policy documents, and routes complex cases to your team.',
    features: [
      { title: 'Plain-language policy answers', body: 'It explains coverage, deductibles and limits using your documents, not generic definitions.' },
      { title: 'Claim first steps', body: 'It tells claimants what to do next and collects their contact details for your adjusters.' },
      { title: 'Escalation with context', body: 'It opens a ticket in your helpdesk or CRM with the full conversation, so nobody starts from zero.' },
    ],
    painPoints: [
      { problem: 'Policyholders call to ask what their policy covers', solution: 'The mascot answers from your exact documents, anytime.' },
      { problem: 'Claimants are stressed and lost', solution: 'A calm, guided conversation tells them the next step.' },
      { problem: 'Insurance sites feel cold and corporate', solution: 'A friendly character makes your brand approachable.' },
    ],
    stats: [
      { value: '24/7', label: 'Policy questions answered' },
      { value: '7', label: 'Helpdesks for escalation' },
      { value: 'Scoped', label: 'To your documents' },
    ],
    faqs: [
      { question: 'Can the mascot explain policy terms?', answer: 'Yes. Upload your policy documents and FAQs and it explains coverage, exclusions and deductibles in plain language.' },
      { question: 'Can it help with claims?', answer: 'It explains the first steps of a claim, collects contact details and escalates to your team with the conversation attached. Claim decisions stay with humans.' },
      { question: 'How do I control what it says?', answer: 'It only uses your knowledge base. Review answers in Activity and fix gaps flagged in Suggestions.' },
    ],
    ctaHeadline: 'Make your policies easy to understand',
    ctaBody: 'Create your insurance mascot free. Trained on your documents in minutes.',
  },

  legal: {
    headline: 'Clients Research Their Problem at 2am. Be the Firm That Answers.',
    subheadline:
      'A 3D AI mascot explains your practice areas, collects case details and books consultations, and it never gives legal advice. That stays with your attorneys.',
    features: [
      { title: 'Practice-area information', body: 'It explains what you handle and how you work, from your own content. It gives information, not advice.' },
      { title: 'Intake before the call', body: 'It collects the facts and contact details when it detects intent, so attorneys start with context.' },
      { title: 'Consultation booking', body: 'With Calendly or Cal.com, prospects book a consultation inside the conversation.' },
    ],
    painPoints: [
      { problem: 'Intake calls eat billable hours', solution: 'The mascot gathers the basics first. Attorneys step in when it matters.' },
      { problem: 'Visitors leave and call another firm', solution: 'An engaging host keeps them on your site until they book.' },
      { problem: 'After-hours enquiries go cold', solution: 'Every late-night enquiry gets captured, with context, for the morning.' },
    ],
    stats: [
      { value: '24/7', label: 'Client capture' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
      { value: '0', label: 'Legal advice given by AI' },
    ],
    faqs: [
      { question: 'Will the mascot give legal advice?', answer: 'No. You configure it to give information about your firm and practice areas only, and to direct anything that needs a professional opinion to a consultation.' },
      { question: 'Can it collect case details?', answer: 'Yes. It captures contact details on detected intent and keeps the full conversation, so your team sees the context before the call.' },
      { question: 'Can prospects book directly?', answer: 'Yes, through Calendly or Cal.com inside the chat.' },
    ],
    ctaHeadline: 'Stop losing after-hours clients',
    ctaBody: 'Create your firm mascot free. Live on your site today, no code.',
  },

  retail: {
    headline: 'Give Every Online Shopper a Personal Stylist.',
    subheadline:
      'A 3D AI mascot asks what shoppers need, recommends the right product, answers sizing and returns questions out loud, and tracks their orders.',
    features: [
      { title: 'Guided product discovery', body: 'It asks a few questions and points to the right item in your catalog.' },
      { title: 'Order and cart answers', body: 'With Shopify connected, it tracks orders and checks cart status in real time.' },
      { title: 'Returns, explained', body: 'It walks customers through your return and exchange policy without a support ticket.' },
    ],
    painPoints: [
      { problem: 'Online shoppers miss in-store advice', solution: 'Your mascot gives that advice, out loud, on every page.' },
      { problem: 'Wrong picks lead to returns', solution: 'Better guidance up front means better choices.' },
      { problem: 'Peak season floods your inbox', solution: 'Recurring questions get absorbed automatically.' },
    ],
    stats: [
      { value: '24/7', label: 'Personal stylist' },
      { value: '3', label: 'Shopify actions: products, orders, cart' },
      { value: '1', label: 'Snippet to install' },
    ],
    faqs: [
      { question: 'How does an AI mascot help retail sales online?', answer: 'It acts like a sales associate. It asks about needs, recommends products and answers sizing, availability and shipping questions in real time.' },
      { question: 'Can it check orders and stock?', answer: 'On Shopify, yes. The integration lets it browse products, track orders and check cart status.' },
      { question: 'Can it match my brand?', answer: 'Yes. Design its look, outfit and voice in the studio so it feels like part of your store.' },
    ],
    ctaHeadline: 'Bring the in-store experience online',
    ctaBody: 'Create your retail mascot free. No technical team needed.',
  },

  saas: {
    headline: "Your Docs Don't Talk. Your Product Mascot Does.",
    subheadline:
      'A 3D AI mascot answers trial users from your docs, explains pricing, books demos with your sales team and handles billing questions through Stripe. Inside your site or your app.',
    features: [
      { title: 'Trained on your docs', body: 'Add your help center, docs and pricing pages. It answers precisely, and flags the questions your docs do not cover.' },
      { title: 'Demo booking in the chat', body: 'It spots buying intent, captures the lead and opens your Calendly or Cal.com slots.' },
      { title: 'Billing without tickets', body: 'With Stripe connected, users check invoices and subscriptions without leaving the conversation.' },
    ],
    painPoints: [
      { problem: 'Trial users get stuck and quietly churn', solution: 'The mascot answers setup questions the moment they get stuck.' },
      { problem: 'Prospects leave pricing pages without booking', solution: 'It answers their objections and offers a demo slot on the spot.' },
      { problem: 'Every SaaS landing page looks the same', solution: 'A talking product mascot is the thing visitors remember and talk about.' },
    ],
    stats: [
      { value: '24/7', label: 'Trial user support' },
      { value: '4', label: 'App SDKs: web, React, React Native, Flutter' },
      { value: 'Stripe', label: 'Billing answers in chat' },
    ],
    faqs: [
      { question: 'Can I put it inside my app, not just my marketing site?', answer: 'Yes. Use the React/Vue package, or React Native or Flutter for mobile, as well as the website snippet.' },
      { question: 'Can it be trained on technical documentation?', answer: 'Yes. Crawl your docs site, upload files or connect Notion. It answers from that content and lists the gaps it finds.' },
      { question: 'Does it connect to our stack?', answer: 'Lead capture, Calendly or Cal.com booking, Stripe billing, Slack alerts, helpdesk escalation and custom webhooks triggered by intent.' },
    ],
    ctaHeadline: 'Give your product a face users remember',
    ctaBody: 'Create your SaaS mascot free. In your site or app in minutes.',
  },

  'digital-agencies': {
    headline: 'Your Clients Will Ask for AI With a Face. Be the Agency That Delivers It.',
    subheadline:
      'Build talking 3D mascots for your clients, each with its own look, voice and knowledge base, all managed from one dashboard. It is a new premium service, not a commodity.',
    features: [
      { title: 'A new premium offer', body: 'Package a branded talking mascot into your web, CRO or AI retainers.' },
      { title: 'One dashboard, many clients', body: 'Projects keep each client separate: their Twins, content, analytics and settings.' },
      { title: 'Installs on whatever they use', body: 'Webflow, WordPress, Shopify, Framer, Wix, Squarespace, React or mobile apps.' },
    ],
    painPoints: [
      { problem: 'Websites and chatbots are turning into commodities', solution: 'A custom talking mascot is something your competitors are not selling yet.' },
      { problem: 'Clients want "AI" but cannot say what it should do', solution: 'Show them a branded mascot that answers, sells and books on their own site.' },
      { problem: 'Building 3D and voice in-house is expensive', solution: 'The studio, voices, hosting and analytics are already built.' },
    ],
    stats: [
      { value: '∞', label: 'Projects on Business' },
      { value: '10', label: 'Twins on Business' },
      { value: '0', label: '3D engineers needed' },
    ],
    faqs: [
      { question: 'Can agencies manage several clients?', answer: 'Yes. Each client gets a Project with its own Twins, knowledge base, widget and analytics. Business and Enterprise plans include unlimited projects.' },
      { question: 'Which platforms can I deploy to?', answer: 'Any website through one snippet, with guides for Webflow, WordPress (plugin), Shopify, Framer, Wix and Squarespace, plus React, React Native and Flutter apps.' },
      { question: 'Is there a partner or affiliate program?', answer: 'Yes. There is an affiliate program with payouts through Stripe Connect, available from your dashboard.' },
    ],
    ctaHeadline: 'Sell the next thing your clients will ask for',
    ctaBody: 'Create your first client mascot free today.',
  },

  'creators-streamers': {
    headline: 'A Co-Host That Never Logs Off.',
    subheadline:
      'Put a talking 3D AI mascot on your stream with a transparent OBS overlay. It reads chat on Twitch, YouTube or Kick and answers out loud. It also runs your website and fan page.',
    features: [
      { title: 'Live on your stream', body: 'Add it as an OBS or Streamlabs browser source with a transparent background, and it animates and talks on its own.' },
      { title: 'Reads your chat', body: 'Connect Twitch, YouTube or Kick and it answers viewers in real time.' },
      { title: 'Your lore, your voice', body: 'Train it on your FAQs and content, design its look and give it a cloned or preset voice.' },
    ],
    painPoints: [
      { problem: 'You cannot answer every chat message and play at the same time', solution: 'Your co-host handles the recurring questions out loud.' },
      { problem: 'Every stream looks the same', solution: 'An AI character with its own personality is a hook viewers remember.' },
      { problem: 'VTuber setups are complex', solution: 'It runs in a browser source. No extra rig, no tracking hardware.' },
    ],
    stats: [
      { value: '4', label: 'Platforms: Twitch, YouTube, Kick, TikTok' },
      { value: 'OBS', label: 'Transparent overlay' },
      { value: '24/7', label: 'On your website too' },
    ],
    faqs: [
      { question: 'How do I add the mascot to my stream?', answer: 'Copy the OBS link from the Deploy page and add it as a browser source in OBS Studio or Streamlabs. The background is transparent.' },
      { question: 'Can it read and answer my chat?', answer: 'Yes. Connect Twitch, YouTube or Kick in the dashboard and it responds to viewers live.' },
      { question: 'Can it sound like me?', answer: 'You can clone a voice from a short recording, or pick one of the preset voices.' },
    ],
    ctaHeadline: 'Give your stream a co-host',
    ctaBody: 'Create your stream mascot free. Add it to OBS in minutes.',
  },

  'coaching-consulting': {
    headline: 'Your Next Client Is Reading Your Site Right Now. Let Your Mascot Talk to Them.',
    subheadline:
      'A 3D AI mascot explains your method and programs, answers the objections that stop high-ticket buyers, qualifies fit and books discovery calls.',
    features: [
      { title: 'Explains your method', body: 'Trained on your programs, case studies and FAQs, it presents your offer the way you would.' },
      { title: 'Qualifies before booking', body: 'It asks the right questions first, then opens your Calendly or Cal.com slots.' },
      { title: 'Signals a premium brand', body: 'A custom character with its own voice tells buyers you are not like the others.' },
    ],
    painPoints: [
      { problem: 'High-ticket buyers leave with unanswered objections', solution: 'The mascot answers them out loud, at the moment of doubt.' },
      { problem: 'Discovery calls with unqualified prospects', solution: 'It qualifies fit before anyone gets on your calendar.' },
      { problem: 'Text chatbots feel cheap', solution: 'A designed character fits a premium brand.' },
    ],
    stats: [
      { value: '24/7', label: 'Discovery call booking' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
      { value: '5 min', label: 'To go live' },
    ],
    faqs: [
      { question: 'How does a mascot help me close more clients?', answer: 'It greets visitors, explains your programs, answers objections, qualifies fit and books calls, so prospects stay engaged instead of leaving with questions.' },
      { question: 'Can it screen prospects?', answer: 'Yes. It asks qualifying questions in the conversation and shares your booking link when the fit is right.' },
      { question: 'How long does it take to train?', answer: 'Add your program pages, brochures and FAQs, and it is ready in minutes. Then refine it with the unanswered questions it reports.' },
    ],
    ctaHeadline: 'Fill your calendar with the right clients',
    ctaBody: 'Create your coaching mascot free. No coding required.',
  },
};
